/** @jsx isolateComponent */

import { useState } from 'react';

import { Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Colors, SkeletonBlock } from '@zapier/design-system';
import { ChangePlan } from '@zapier/design-system-revenue/src/components/other';

import {
  AccountPlan,
  TrialExtensionReason,
} from '@zapier/design-system-revenue/src/types';

import { useCurrentAccount, useExtendTrial } from '../hooks';
import { Celebrate } from './Celebrate';
import { UpgradeReason } from './UpgradeReason';

const MODAL_STATE = {
  reasons: 'reasons',
  upgrade: 'upgrade',
  celebrateTrial: 'celebrateTrial',
  celebrateUpgrade: 'celebrateUpgrade',
  error: 'error',
};

type Props = {
  appName?: string;
  onBack: () => void;
  onClose: () => void;
  onUpgradeFinish: () => void;
  upgradeToPlan: AccountPlan;
  validationReason: TrialExtensionReason;
};

export const UpgradeModal = ({
  appName,
  onBack,
  onClose,
  onUpgradeFinish,
  upgradeToPlan,
  validationReason,
}: Props) => {
  const [modalState, setModalState] = useState(MODAL_STATE.reasons);

  // Queries and mutations ---------------------------
  const { currentAccount, isLoading: isLoadingAccount } = useCurrentAccount();
  const {
    extendTrial,
    error: extendTrialError,
    isLoading: isExtendingTrial,
  } = useExtendTrial({
    reason: validationReason,
    shouldAutoUpgrade: true,
  });
  const isLoading = isLoadingAccount || isExtendingTrial;

  const canExtendTrial =
    !currentAccount?.isPaying &&
    !currentAccount?.enabledTrialExtensions?.includes(validationReason);

  const onExtendTrial = async () => {
    try {
      await extendTrial();
      setModalState(MODAL_STATE.celebrateTrial);
    } catch {
      setModalState(MODAL_STATE.error);
    }
  };

  const onUpgradeClick = () => {
    setModalState(MODAL_STATE.upgrade);
  };

  const onUpgradeComplete = () => {
    onUpgradeFinish();
    setModalState(MODAL_STATE.celebrateUpgrade);
  };

  const stateToModalContents = {
    [MODAL_STATE.reasons]: (
      <UpgradeReason
        appName={appName}
        canExtendTrial={canExtendTrial}
        onBack={onBack}
        onExtendTrial={onExtendTrial}
        onUpgradeClick={onUpgradeClick}
        plan={upgradeToPlan}
        reason={validationReason}
      />
    ),
    [MODAL_STATE.upgrade]: (
      <ChangePlan
        changeType={'upgrade'}
        isCompact={true}
        onCancel={onClose}
        onFinishChangePlan={onUpgradeComplete}
        showSuccessModal={false}
        toPlanName={(upgradeToPlan?.name ?? '').toLowerCase()}
      />
    ),
    [MODAL_STATE.celebrateTrial]: (
      <Celebrate onClose={onClose} plan={upgradeToPlan} reason="trial" />
    ),
    [MODAL_STATE.celebrateUpgrade]: (
      <Celebrate onClose={onClose} plan={upgradeToPlan} reason="upgrade" />
    ),
    [MODAL_STATE.error]: (
      <Celebrate
        errorMessage={extendTrialError}
        onClose={onClose}
        plan={upgradeToPlan}
        reason="error"
      />
    ),
  };

  const modalContents = stateToModalContents[modalState];

  return (
    <Modal
      backgroundColor={Colors.grey000}
      closeOnOutsideClick={true}
      maxWidth={895}
      onClose={onClose}
    >
      {isLoading ? (
        <SkeletonBlock height={335} width="100%" />
      ) : (
        <div
          css={{
            backgroundColor: Colors.grey000,
          }}
        >
          {modalContents}
        </div>
      )}
    </Modal>
  );
};
